import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Skeleton,
  Chip,
  Button,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  DragIndicator as DragIndicatorIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { BASE_URL } from "../../../services/configUrls";
import AddFeatureToInstituteModal from "./AddFeatureToInstituteModal";
import CreateTextStatusModalForNew from "./CreateTextStatusModalForMem";

const ViewFeaturesModal = ({ open, onClose, instituteId }) => {
  const [loading, setLoading] = useState(false);
  const [featuresData, setFeaturesData] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  const [orderedFeatures, setOrderedFeatures] = useState([]);
  const [openAddFeature, setOpenAddFeature] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [editingFeatureId, setEditingFeatureId] = useState(null);
  const [tempStatusValue, setTempStatusValue] = useState("");
  const [updateLoading, setUpdateLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [countError, setCountError] = useState(false);
  const [countErrorMessage, setCountErrorMessage] = useState("");

  const [openCreateStatusModal, setOpenCreateStatusModal] = useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");

  const prevInstituteIdRef = useRef(null);

  const fetchFeatures = useCallback(async () => {
    if (!instituteId) return;

    setLoading(true);
    setEditingFeatureId(null);
    setTempStatusValue("");
    setCountError(false);
    setCountErrorMessage("");

    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/institute/${instituteId}/features`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          params: {
            _t: new Date().getTime(),
          },
        }
      );

      if (instituteId === prevInstituteIdRef.current) {
        setFeaturesData(response.data);
        setOrderedFeatures(response.data?.features || []);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
      if (instituteId === prevInstituteIdRef.current) {
        setFeaturesData(null);
        setFilteredFeatures([]);
        setOrderedFeatures([]);
      }
    } finally {
      setLoading(false);
      setUpdateLoading(null);
      setDeleteLoading(null);
    }
  }, [instituteId]);

  useEffect(() => {
    if (open && instituteId) {
      const instituteChanged = prevInstituteIdRef.current !== instituteId;
      prevInstituteIdRef.current = instituteId;

      setSearchInput("");
      setUpdateLoading(null);
      setDeleteLoading(null);
      setEditingFeatureId(null);
      setTempStatusValue("");
      setFeaturesData(null);
      setFilteredFeatures([]);
      setOrderedFeatures([]);
      setOpenAddFeature(false);
      setCountError(false);
      setCountErrorMessage("");
      setReorderLoading(false);

      if (instituteChanged) {
        setRefreshTrigger(0);
      }

      fetchFeatures();
      fetchStatusOptions();
    } else if (!open) {
      prevInstituteIdRef.current = null;
      setSearchInput("");
      setUpdateLoading(null);
      setDeleteLoading(null);
      setEditingFeatureId(null);
      setTempStatusValue("");
      setFeaturesData(null);
      setFilteredFeatures([]);
      setOrderedFeatures([]);
      setOpenAddFeature(false);
      setRefreshTrigger(0);
      setCountError(false);
      setCountErrorMessage("");
      setReorderLoading(false);
    }
  }, [open, instituteId, fetchFeatures]);

  useEffect(() => {
    if (refreshTrigger > 0 && open && instituteId) {
      fetchFeatures();
    }
  }, [refreshTrigger, open, instituteId, fetchFeatures]);

  useEffect(() => {
    if (featuresData?.features) {
      const searchTerm = searchInput.trim().toLowerCase();
      if (searchTerm === "") {
        const orderedIds = orderedFeatures.map((f) => f.feature_id);
        const available = featuresData.features.filter(
          (f) => !orderedIds.includes(f.feature_id)
        );
        setFilteredFeatures(available);
      } else {
        const orderedIds = orderedFeatures.map((f) => f.feature_id);
        const filtered = featuresData.features.filter(
          (feature) =>
            feature.feature_name.toLowerCase().includes(searchTerm) &&
            !orderedIds.includes(feature.feature_id)
        );
        setFilteredFeatures(filtered);
      }
    } else {
      setFilteredFeatures([]);
    }
  }, [searchInput, featuresData, orderedFeatures]);

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
        }));
        setStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleAddToOrder = (feature) => {
    setOrderedFeatures((prev) => [...prev, feature]);
  };

  const handleRemoveFromOrder = (featureId) => {
    setOrderedFeatures((prev) =>
      prev.filter((f) => f.feature_id !== featureId)
    );
    if (editingFeatureId === featureId) {
      setEditingFeatureId(null);
      setTempStatusValue("");
      setCountError(false);
      setCountErrorMessage("");
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrdered = [...orderedFeatures];
    [newOrdered[index - 1], newOrdered[index]] = [
      newOrdered[index],
      newOrdered[index - 1],
    ];
    setOrderedFeatures(newOrdered);
  };

  const handleMoveDown = (index) => {
    if (index === orderedFeatures.length - 1) return;
    const newOrdered = [...orderedFeatures];
    [newOrdered[index], newOrdered[index + 1]] = [
      newOrdered[index + 1],
      newOrdered[index],
    ];
    setOrderedFeatures(newOrdered);
  };

  const handleStatusEdit = (feature) => {
    setUpdateLoading(null);
    setDeleteLoading(null);
    setCountError(false);
    setCountErrorMessage("");

    setEditingFeatureId(feature.feature_id);
    if (feature.feature_type === "Text") {
      setTempStatusValue(feature.status_text || "");
    } else {
      setTempStatusValue(feature.value_count?.toString() || "0");
    }
  };

  const handleStatusSave = async (feature) => {
    if (countError) {
      setSnackbar({
        open: true,
        message: "Please fix the errors before saving",
        severity: "error",
      });
      return;
    }

    if (feature.feature_type === "Count") {
      const numValue = parseInt(tempStatusValue);
      if (isNaN(numValue) || numValue < 0) {
        setSnackbar({
          open: true,
          message: "Please enter a valid number for count",
          severity: "error",
        });
        return;
      }
    }

    if (!tempStatusValue.trim()) {
      setSnackbar({
        open: true,
        message: "Value cannot be empty",
        severity: "error",
      });
      return;
    }

    setUpdateLoading(feature.feature_id);

    try {
      const accessToken = localStorage.getItem("accessToken");

      let payload;
      if (feature.feature_type === "Count") {
        payload = {
          value_text: null,
          value_count: parseInt(tempStatusValue),
        };
      } else {
        payload = {
          value_text: tempStatusValue,
          value_count: null,
        };
      }

      const response = await fetch(
        `${BASE_URL}/accounts/institute/${instituteId}/feature/${feature.feature_id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Feature updated successfully",
          severity: "success",
        });

        setEditingFeatureId(null);
        setTempStatusValue("");
        setUpdateLoading(null);
        setCountError(false);
        setCountErrorMessage("");
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setUpdateLoading(null);
        setSnackbar({
          open: true,
          message: result.message || "Failed to update feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating feature:", error);
      setUpdateLoading(null);
      setSnackbar({
        open: true,
        message: "Error updating feature. Please try again.",
        severity: "error",
      });
    }
  };

  const handleStatusCancel = () => {
    setEditingFeatureId(null);
    setTempStatusValue("");
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleDeleteFeature = async (feature) => {
    if (
      !window.confirm(
        `Are you sure you want to remove "${feature.feature_name}"?`
      )
    ) {
      return;
    }

    setDeleteLoading(feature.feature_id);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/institute/${instituteId}/feature/${feature.feature_id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Feature removed successfully",
          severity: "success",
        });

        setDeleteLoading(null);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setDeleteLoading(null);
        setSnackbar({
          open: true,
          message: result.message || "Failed to remove feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error removing feature:", error);
      setDeleteLoading(null);
      setSnackbar({
        open: true,
        message: "Error removing feature. Please try again.",
        severity: "error",
      });
    }
  };

  const handleOpenAddFeature = () => {
    setOpenAddFeature(true);
  };

  const handleCloseAddFeature = () => {
    setOpenAddFeature(false);
  };

  const handleFeatureAdded = async () => {
    setOpenAddFeature(false);

    setSnackbar({
      open: true,
      message: "Features added successfully",
      severity: "success",
    });

    setFeaturesData(null);
    setFilteredFeatures([]);
    setOrderedFeatures([]);

    await new Promise((resolve) => setTimeout(resolve, 300));

    setRefreshTrigger((prev) => prev + 1);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenCreateStatusModal = () => {
    setOpenCreateStatusModal(true);
  };

  const handleCloseCreateStatusModal = () => {
    setOpenCreateStatusModal(false);
  };

  const handleCreateTextStatus = () => {
    // This will be called after status is created
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const reorderedFeatures = Array.from(orderedFeatures);
    const [movedFeature] = reorderedFeatures.splice(sourceIndex, 1);
    reorderedFeatures.splice(destinationIndex, 0, movedFeature);

    setOrderedFeatures(reorderedFeatures);

    const payload = reorderedFeatures.map((feature, index) => ({
      feature_id: feature.feature_id,
      display_order: index + 1,
    }));

    setReorderLoading(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/institute/${instituteId}/features/reorder`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Features reordered successfully",
          severity: "success",
        });

        setRefreshTrigger((prev) => prev + 1);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to reorder features",
          severity: "error",
        });
        setRefreshTrigger((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error reordering features:", error);
      setSnackbar({
        open: true,
        message: "Error reordering features. Please try again.",
        severity: "error",
      });

      setRefreshTrigger((prev) => prev + 1);
    } finally {
      setReorderLoading(false);
    }
  };

  const handleSaveOrder = async () => {
    const payload = orderedFeatures.map((feature, index) => ({
      feature_id: feature.feature_id,
      display_order: index + 1,
    }));

    setReorderLoading(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/institute/${instituteId}/features/reorder`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Display order saved successfully",
          severity: "success",
        });

        setRefreshTrigger((prev) => prev + 1);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to save display order",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error saving display order:", error);
      setSnackbar({
        open: true,
        message: "Error saving display order. Please try again.",
        severity: "error",
      });
    } finally {
      setReorderLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxHeight: "90vh",
            width: "1400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E5E7EB",
            pb: 1.5,
            pt: 2,
            px: 2.5,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#1F1F1F",
                mb: 0.5,
              }}
            >
              Institute Features Management
            </Typography>
            {featuresData && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  fontWeight: 400,
                }}
              >
                {featuresData.membership_name}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5, mt: 3 }}>
          <Grid container spacing={2.5}>
            {/* Left Side - Search & Add Features */}
            <Grid item xs={12} md={5}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#1F1F1F",
                    }}
                  >
                    Available Features
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddFeature}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontSize: "11px",
                      fontWeight: 500,
                      backgroundColor: "#3B82F6",
                      "&:hover": { backgroundColor: "#2563EB" },
                      boxShadow: "none",
                      py: 0.5,
                      px: 1.5,
                    }}
                  >
                    Create New
                  </Button>
                </Box>

                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search features..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  sx={{
                    mb: 1.5,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F9FAFB",
                      fontSize: "12px",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Paper
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    maxHeight: "520px",
                    overflowY: "auto",
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {loading ? (
                      Array.from(new Array(5)).map((_, index) => (
                        <ListItem key={index} sx={{ py: 1.5, px: 1.5 }}>
                          <Skeleton width="100%" height={36} />
                        </ListItem>
                      ))
                    ) : filteredFeatures.length === 0 ? (
                      <ListItem sx={{ py: 1.5, px: 1.5 }}>
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#6B7280",
                                textAlign: "center",
                                py: 3,
                              }}
                            >
                              {searchInput.trim()
                                ? `No features found matching "${searchInput}"`
                                : "All features added to display order"}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ) : (
                      filteredFeatures.map((feature, index) => (
                        <React.Fragment key={feature.feature_id}>
                          <ListItem
                            sx={{
                              "&:hover": {
                                backgroundColor: "#F9FAFB",
                              },
                              py: 1.2,
                              px: 1.5,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#1F1F1F",
                                  }}
                                >
                                  {feature.feature_name}
                                </Typography>
                              }
                              secondary={
                                <Chip
                                  label={feature.feature_type}
                                  size="small"
                                  sx={{
                                    fontSize: "10px",
                                    height: "18px",
                                    mt: 0.5,
                                    fontWeight: 500,
                                    backgroundColor:
                                      feature.feature_type === "Text"
                                        ? "#DBEAFE"
                                        : "#FEF3C7",
                                    color:
                                      feature.feature_type === "Text"
                                        ? "#1E40AF"
                                        : "#A16207",
                                  }}
                                />
                              }
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleAddToOrder(feature)}
                              sx={{
                                color: "#3B82F6",
                                "&:hover": {
                                  backgroundColor: "#EFF6FF",
                                },
                              }}
                            >
                              <AddCircleOutlineIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                          </ListItem>
                          {index < filteredFeatures.length - 1 && <Divider />}
                        </React.Fragment>
                      ))
                    )}
                  </List>
                </Paper>

                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "#6B7280",
                    mt: 1,
                    fontStyle: "italic",
                  }}
                >
                  💡 Click + icon to add features to display order
                </Typography>
              </Box>
            </Grid>

            {/* Right Side - Display Order & Management */}
            <Grid item xs={12} md={7}>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1F1F1F",
                      }}
                    >
                      Display Order & Edit Values
                    </Typography>
                    {orderedFeatures.length > 0 && (
                      <Chip
                        label={`${orderedFeatures.length} features`}
                        size="small"
                        sx={{
                          fontSize: "10px",
                          height: "20px",
                          fontWeight: 500,
                          backgroundColor: "#EFF6FF",
                          color: "#1E40AF",
                        }}
                      />
                    )}
                  </Box>
                  {orderedFeatures.length > 0 && (
                    <Button
                      variant="contained"
                      onClick={handleSaveOrder}
                      disabled={reorderLoading}
                      size="small"
                      sx={{
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 500,
                        backgroundColor: "#10B981",
                        "&:hover": { backgroundColor: "#059669" },
                        boxShadow: "none",
                        py: 0.5,
                        px: 1.5,
                      }}
                    >
                      {reorderLoading ? (
                        <CircularProgress
                          size={14}
                          color="inherit"
                          sx={{ mr: 0.5 }}
                        />
                      ) : null}
                      Save Display Order
                    </Button>
                  )}
                </Box>

                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="features-reorder">
                    {(provided, snapshot) => (
                      <Paper
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        elevation={0}
                        sx={{
                          border: snapshot.isDraggingOver
                            ? "2px dashed #3B82F6"
                            : "1px solid #E5E7EB",
                          borderRadius: "6px",
                          maxHeight: "520px",
                          overflowY: "auto",
                          backgroundColor: snapshot.isDraggingOver
                            ? "#F0F9FF"
                            : "#FFFFFF",
                          transition: "all 0.2s ease",
                          p: orderedFeatures.length > 0 ? 1.2 : 0,
                        }}
                      >
                        {orderedFeatures.length === 0 ? (
                          <Box
                            sx={{
                              py: 8,
                              px: 2,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#6B7280",
                                fontWeight: 400,
                                mb: 0.5,
                              }}
                            >
                              No features in display order
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#9CA3AF",
                                fontStyle: "italic",
                              }}
                            >
                              Add features from the left panel to set their
                              display order
                            </Typography>
                          </Box>
                        ) : (
                          orderedFeatures.map((feature, index) => (
                            <Draggable
                              key={feature.feature_id}
                              draggableId={feature.feature_id.toString()}
                              index={index}
                              isDragDisabled={
                                reorderLoading ||
                                editingFeatureId === feature.feature_id
                              }
                            >
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    mb: 1.2,
                                    backgroundColor: snapshot.isDragging
                                      ? "#EFF6FF"
                                      : "#FFFFFF",
                                    boxShadow: snapshot.isDragging
                                      ? "0 6px 20px rgba(0,0,0,0.1)"
                                      : "0 1px 3px rgba(0,0,0,0.05)",
                                    border: "1px solid #E5E7EB",
                                    borderRadius: "6px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      boxShadow: "0 3px 10px rgba(0,0,0,0.07)",
                                    },
                                  }}
                                >
                                  <Box sx={{ p: 1.5 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1.2,
                                      }}
                                    >
                                      {/* Display Order Number */}
                                      <Box
                                        sx={{
                                          minWidth: "34px",
                                          height: "34px",
                                          borderRadius: "5px",
                                          backgroundColor: "#3B82F6",
                                          color: "#FFFFFF",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: "14px",
                                          fontWeight: 700,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {index + 1}
                                      </Box>

                                      {/* Drag Handle */}
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{
                                          cursor: "grab",
                                          display: "flex",
                                          alignItems: "center",
                                          color: "#9CA3AF",
                                          "&:hover": { color: "#6B7280" },
                                          "&:active": { cursor: "grabbing" },
                                          flexShrink: 0,
                                        }}
                                      >
                                        <DragIndicatorIcon
                                          sx={{ fontSize: 20 }}
                                        />
                                      </Box>

                                      {/* Feature Info */}
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.8,
                                            mb: 0.5,
                                          }}
                                        >
                                          <Typography
                                            sx={{
                                              fontSize: "13px",
                                              fontWeight: 600,
                                              color: "#1F1F1F",
                                            }}
                                          >
                                            {feature.feature_name}
                                          </Typography>
                                          <Chip
                                            label={feature.feature_type}
                                            size="small"
                                            sx={{
                                              fontSize: "9px",
                                              height: "18px",
                                              fontWeight: 500,
                                              backgroundColor:
                                                feature.feature_type === "Text"
                                                  ? "#DBEAFE"
                                                  : "#FEF3C7",
                                              color:
                                                feature.feature_type === "Text"
                                                  ? "#1E40AF"
                                                  : "#A16207",
                                            }}
                                          />
                                        </Box>

                                        {/* Value Display/Edit */}
                                        {editingFeatureId ===
                                        feature.feature_id ? (
                                          <Box
                                            sx={{
                                              mt: 0.8,
                                              display: "flex",
                                              gap: 0.8,
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            {feature.feature_type ===
                                            "Count" ? (
                                              <TextField
                                                autoFocus
                                                size="small"
                                                type="number"
                                                value={tempStatusValue}
                                                onChange={(e) => {
                                                  setTempStatusValue(
                                                    e.target.value
                                                  );
                                                  const newValue =
                                                    e.target.value;
                                                  const numValue =
                                                    parseInt(newValue);
                                                  if (
                                                    newValue === "" ||
                                                    isNaN(numValue)
                                                  ) {
                                                    setCountError(true);
                                                    setCountErrorMessage(
                                                      "Please enter a valid count"
                                                    );
                                                  } else if (numValue < 1) {
                                                    setCountError(true);
                                                    setCountErrorMessage(
                                                      "Count must be at least 1"
                                                    );
                                                  } else {
                                                    setCountError(false);
                                                    setCountErrorMessage("");
                                                  }
                                                }}
                                                error={countError}
                                                helperText={
                                                  countError
                                                    ? countErrorMessage
                                                    : ""
                                                }
                                                sx={{
                                                  flex: 1,
                                                  "& .MuiInputBase-input": {
                                                    fontSize: "12px",
                                                  },
                                                }}
                                              />
                                            ) : (
                                              <Autocomplete
                                                freeSolo
                                                autoFocus
                                                options={statusOptions}
                                                value={tempStatusValue}
                                                getOptionLabel={(option) => {
                                                  if (
                                                    typeof option === "string"
                                                  )
                                                    return option;
                                                  return option.text || "";
                                                }}
                                                onChange={(event, newValue) => {
                                                  if (newValue) {
                                                    const textValue =
                                                      typeof newValue ===
                                                      "string"
                                                        ? newValue
                                                        : newValue.text;
                                                    setTempStatusValue(
                                                      textValue
                                                    );
                                                  }
                                                }}
                                                onInputChange={(
                                                  event,
                                                  newInputValue
                                                ) => {
                                                  setTempStatusValue(
                                                    newInputValue
                                                  );
                                                }}
                                                renderInput={(params) => (
                                                  <TextField
                                                    {...params}
                                                    size="small"
                                                    placeholder="Select or type"
                                                    sx={{
                                                      "& .MuiInputBase-input": {
                                                        fontSize: "12px",
                                                      },
                                                    }}
                                                  />
                                                )}
                                                sx={{ flex: 1 }}
                                              />
                                            )}
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleStatusSave(feature)
                                              }
                                              disabled={
                                                countError ||
                                                updateLoading ===
                                                  feature.feature_id
                                              }
                                              sx={{
                                                color: "#10B981",
                                                backgroundColor: "#D1FAE5",
                                                "&:hover": {
                                                  backgroundColor: "#A7F3D0",
                                                },
                                                width: "30px",
                                                height: "30px",
                                              }}
                                            >
                                              {updateLoading ===
                                              feature.feature_id ? (
                                                <CircularProgress size={14} />
                                              ) : (
                                                <CheckIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              )}
                                            </IconButton>
                                            <IconButton
                                              size="small"
                                              onClick={handleStatusCancel}
                                              sx={{
                                                color: "#EF4444",
                                                backgroundColor: "#FEE2E2",
                                                "&:hover": {
                                                  backgroundColor: "#FECACA",
                                                },
                                                width: "30px",
                                                height: "30px",
                                              }}
                                            >
                                              <CloseIcon
                                                sx={{ fontSize: 16 }}
                                              />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              mt: 0.8,
                                              display: "inline-flex",
                                              alignItems: "center",
                                              px: 1.5,
                                              py: 0.6,
                                              borderRadius: "4px",
                                              backgroundColor:
                                                feature.feature_type === "Text"
                                                  ? "#E0E7FF"
                                                  : "#FEF3C7",
                                              color:
                                                feature.feature_type === "Text"
                                                  ? "#3730A3"
                                                  : "#A16207",
                                              fontWeight: 500,
                                              fontSize: "12px",
                                              cursor: "pointer",
                                              gap: 0.5,
                                              "&:hover": {
                                                opacity: 0.8,
                                              },
                                            }}
                                            onClick={() =>
                                              handleStatusEdit(feature)
                                            }
                                          >
                                            {feature.feature_type === "Text" ? (
                                              <>
                                                {feature.status_symbol && (
                                                  <Typography
                                                    component="span"
                                                    sx={{ fontSize: "14px" }}
                                                  >
                                                    {feature.status_symbol}
                                                  </Typography>
                                                )}
                                                {feature.status_text || "-"}
                                              </>
                                            ) : feature.feature_type ===
                                              "Count" ? (
                                              feature.value_count !== null ? (
                                                feature.value_count.toLocaleString()
                                              ) : (
                                                "-"
                                              )
                                            ) : (
                                              "-"
                                            )}
                                            <EditIcon
                                              sx={{
                                                fontSize: 12,
                                                ml: 0.3,
                                                opacity: 0.6,
                                              }}
                                            />
                                          </Box>
                                        )}
                                      </Box>

                                      {/* Action Buttons */}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.5,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {/* Up/Down Arrows */}
                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleMoveUp(index)}
                                            disabled={
                                              index === 0 ||
                                              reorderLoading ||
                                              editingFeatureId ===
                                                feature.feature_id
                                            }
                                            sx={{
                                              color: "#3B82F6",
                                              backgroundColor: "#EFF6FF",
                                              "&:hover": {
                                                backgroundColor: "#DBEAFE",
                                              },
                                              "&:disabled": { opacity: 0.3 },
                                              width: "26px",
                                              height: "26px",
                                            }}
                                            title="Move up"
                                          >
                                            <ArrowUpwardIcon
                                              sx={{ fontSize: 14 }}
                                            />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleMoveDown(index)
                                            }
                                            disabled={
                                              index ===
                                                orderedFeatures.length - 1 ||
                                              reorderLoading ||
                                              editingFeatureId ===
                                                feature.feature_id
                                            }
                                            sx={{
                                              color: "#3B82F6",
                                              backgroundColor: "#EFF6FF",
                                              "&:hover": {
                                                backgroundColor: "#DBEAFE",
                                              },
                                              "&:disabled": { opacity: 0.3 },
                                              width: "26px",
                                              height: "26px",
                                            }}
                                            title="Move down"
                                          >
                                            <ArrowDownwardIcon
                                              sx={{ fontSize: 14 }}
                                            />
                                          </IconButton>
                                        </Box>

                                        {/* Remove/Delete */}
                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleRemoveFromOrder(
                                                feature.feature_id
                                              )
                                            }
                                            disabled={
                                              editingFeatureId ===
                                              feature.feature_id
                                            }
                                            sx={{
                                              color: "#F59E0B",
                                              backgroundColor: "#FEF3C7",
                                              "&:hover": {
                                                backgroundColor: "#FDE68A",
                                              },
                                              width: "26px",
                                              height: "26px",
                                            }}
                                            title="Remove from order"
                                          >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDeleteFeature(feature)
                                            }
                                            disabled={
                                              deleteLoading ===
                                                feature.feature_id ||
                                              editingFeatureId ===
                                                feature.feature_id
                                            }
                                            sx={{
                                              color: "#DC2626",
                                              backgroundColor: "#FEE2E2",
                                              "&:hover": {
                                                backgroundColor: "#FECACA",
                                              },
                                              width: "26px",
                                              height: "26px",
                                            }}
                                            title="Delete permanently"
                                          >
                                            {deleteLoading ===
                                            feature.feature_id ? (
                                              <CircularProgress
                                                size={12}
                                                color="error"
                                              />
                                            ) : (
                                              <DeleteIcon
                                                sx={{ fontSize: 14 }}
                                              />
                                            )}
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Card>
                              )}
                            </Draggable>
                          ))
                        )}
                        {provided.placeholder}
                      </Paper>
                    )}
                  </Droppable>
                </DragDropContext>

                {orderedFeatures.length > 0 && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      backgroundColor: "#F9FAFB",
                      borderRadius: "5px",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "#6B7280",
                        fontWeight: 500,
                        mb: 0.8,
                      }}
                    >
                      💡 Quick Tips:
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        fontSize: "10px",
                        color: "#6B7280",
                        pl: 2,
                        m: 0,
                        lineHeight: 1.6,
                      }}
                    >
                      <li>Drag cards or use ↑↓ arrows to reorder</li>
                      <li>Click colored value box to edit</li>
                      <li>Yellow X = Remove from order (keeps feature)</li>
                      <li>Red trash = Delete permanently</li>
                      <li>Click "Save Display Order" when done</li>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Add Feature Modal */}
      <AddFeatureToInstituteModal
        open={openAddFeature}
        onClose={handleCloseAddFeature}
        instituteId={instituteId}
        existingFeatures={featuresData?.features || []}
        onFeatureAdded={handleFeatureAdded}
      />

      {/* Create Text Status Modal */}
      <CreateTextStatusModalForNew
        open={openCreateStatusModal}
        onClose={handleCloseCreateStatusModal}
        newStatusText={newStatusText}
        setNewStatusText={setNewStatusText}
        newStatusSymbol={newStatusSymbol}
        setNewStatusSymbol={setNewStatusSymbol}
        onCreateTextStatus={handleCreateTextStatus}
        onRefreshStatusOptions={fetchStatusOptions}
        onShowSnackbar={setSnackbar}
        onResetTempValue={(val) => {
          if (val) setTempStatusValue(val);
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ViewFeaturesModal;
